import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { getPipeline, getPipelineDetail, getCrossEventLeads, createSalesAssignment, createMarketingAssignment, patchSalesAssignment, getUsers, getMarketingAssignments, getSalesAssignments } from "../services/api";
import { Search, X, AlertTriangle, ClipboardList, Mail, Building2, CheckCircle2, User, RefreshCw, Clock, Sparkles, Pencil } from 'lucide-react';
import { FilterSelect } from "../components/FilterBar";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";


const STATUS_C = {
  "Done":        ["#15803d","#dcfce7"],
  "In Progress": ["#E8431A","#FCEBE5"],
  "Behind":      ["#9a5b00","#fef4e6"],
  "Not Started": ["#818084","#F4F4F5"],
  "Unassigned":  ["#C1121F","#fdecec"],
};


function StatusChip({ s }) {
  const [c, bg] = STATUS_C[s] || ["#818084","#F4F4F5"];
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:9.5,fontWeight:700,color:c,background:bg,padding:"4px 10px",borderRadius:20,whiteSpace:"nowrap",letterSpacing:0.1}}>
      <span style={{width:6,height:6,borderRadius:"50%",background:c,display:"inline-block"}}/>
      {s}
    </span>
  );
}

function PBar({ pct }) {
  return (
    <div style={{height:5,background:"#F4F4F5",borderRadius:3,overflow:"hidden"}}>
      <div style={{height:"100%",width:`${Math.min(pct,100)}%`,background:pct>=60?"#16a34a":pct>=30?"#F18F18":"#F5A93A",borderRadius:3}}/>
    </div>
  );
}

export default function PipelinePage() {
  const {
    selectedEvent, changeEvent, nav,
    currentRole, setCurrentRole, currentUserId,
    notifs, setNotifs, notifOpen, setNotifOpen,
    setPendingInsights, setPendingLeadsShow,
  } = useApp();

  const [pipelineYear,  setPipelineYear]  = useState(2026);
  const [pipelineEvent, setPipelineEvent] = useState(null);
  const [eventSearch,   setEventSearch]   = useState("");
  const [drawerShow,    setDrawerShow]    = useState(null);
  const [pipelineData,  setPipelineData]  = useState([]);
  const [pipelineError, setPipelineError] = useState(null);
  const [detailData,    setDetailData]    = useState({ shows: [], team: [] });
  const detailShows = detailData.shows || [];
  const [detailLoading, setDetailLoading] = useState(false);
  const [users,         setUsers]         = useState([]);
  const [assignModal,   setAssignModal]   = useState(null); // {show_id, show_name}
  const [assignForm,    setAssignForm]    = useState({ owner_id: "", review_type: "online_review", deadline: "" });
  const [assignSaving,  setAssignSaving]  = useState(false);
  const [assignError,   setAssignError]   = useState("");
  const [mktAssignments,    setMktAssignments]    = useState([]);
  const [mktDetailShows,    setMktDetailShows]    = useState([]);
  const [mktUsers,          setMktUsers]          = useState([]);
  const [myAssignments,     setMyAssignments]     = useState([]);
  const [crossEventLeads,   setCrossEventLeads]   = useState([]);

  useEffect(() => {
    getUsers({ role: "sales_manager" })
      .then(data => { if (Array.isArray(data)) setUsers(data); })
      .catch(() => {});
    // load marketing managers; fall back to all users if none found
    getUsers({ role: "marketing_manager" })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setMktUsers(data);
        } else {
          getUsers().then(all => { if (Array.isArray(all)) setMktUsers(all); }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (currentRole !== "Marketing Head") return;
    getMarketingAssignments({ year: pipelineYear })
      .then(data => { if (Array.isArray(data)) setMktAssignments(data); })
      .catch(() => {});
  }, [currentRole, pipelineYear]);

  useEffect(() => {
    const isSM = currentRole === "Sales Manager";
    const isMM = currentRole === "Marketing Manager";
    if (!isSM && !isMM) { setMyAssignments([]); return; }
    const ownerId = isSM ? users[0]?.user_id : mktUsers[0]?.user_id;
    if (!ownerId) return;
    const fetchFn = isSM ? getSalesAssignments : getMarketingAssignments;
    fetchFn({ owner_id: ownerId, year: pipelineYear })
      .then(data => { if (Array.isArray(data)) setMyAssignments(data); })
      .catch(() => {});
  }, [currentRole, pipelineYear, users, mktUsers]);

  const isMH  = currentRole === "Marketing Head";
  const isSM  = currentRole === "Sales Manager";
  const isMM  = currentRole === "Marketing Manager";
  const isMgr = isSM || isMM;
  const isSH  = !isMH && !isMgr;

  const refreshDetail = () => {
    if (!pipelineEvent) return;
    getPipelineDetail(pipelineEvent, { year: pipelineYear })
      .then(d => setDetailData(d))
      .catch(() => {});
  };

  const refreshMktDetail = () => {
    if (!pipelineEvent) return;
    getMarketingAssignments({ ite_event_id: pipelineEvent })
      .then(data => { if (Array.isArray(data)) setMktDetailShows(data); })
      .catch(() => {});
  };

  const handleAssign = () => {
    if (!assignForm.owner_id) { setAssignError("Please select an owner."); return; }
    setAssignSaving(true);
    setAssignError("");
    const isEdit = !!assignModal.isEdit;
    const p = isEdit
      ? patchSalesAssignment(assignModal.show_id, {
          owner_id:    assignForm.owner_id,
          review_type: assignForm.review_type,
          deadline:    assignForm.deadline || null,
        })
      : (isMH ? createMarketingAssignment : createSalesAssignment)({
          show_id:     assignModal.show_id,
          owner_id:    assignForm.owner_id,
          review_type: isMH ? "campaign_review" : assignForm.review_type,
          deadline:    assignForm.deadline || null,
          created_by:  currentUserId,
        });
    p
      .then(() => {
        setAssignModal(null);
        setAssignForm({ owner_id: "", review_type: "online_review", deadline: "" });
        if (isMH) {
          refreshMktDetail();
        } else {
          refreshDetail();
        }
      })
      .catch(e => setAssignError(e?.message || "Failed to assign — please try again."))
      .finally(() => setAssignSaving(false));
  };

  useEffect(() => {
    getPipeline({ year: pipelineYear })
      .then(data => {
        setPipelineError(null);
        if (Array.isArray(data)) {
          setPipelineData(data.map(e => ({
            id:                  e.ite_event_id,
            name:                e.event_name,
            sector:              e.sector ?? "",
            totalShows:          e.total_shows          ?? 0,
            totalCompanies:      e.total_companies      ?? 0,
            pendingEnrichment:   e.pending_enrichment    ?? 0,
            awaitingReview:      e.awaiting_review       ?? 0,
            inReview:            e.in_review             ?? 0,
            complete:            e.complete_shows        ?? 0,
            overdue:             e.overdue_shows         ?? 0,
            crmApproved:         e.crm_approved          ?? 0,
            crmPushed:           e.crm_pushed            ?? 0,
            mktAwaitingReview:   e.mkt_awaiting_review   ?? 0,
            mktInReview:         e.mkt_in_review         ?? 0,
            mktComplete:         e.mkt_complete          ?? 0,
            shows:               [],
          })));
        }
      })
      .catch(err => setPipelineError(err?.message || "Failed to load pipeline"));
  }, [pipelineYear]);

  const selEvent = pipelineEvent ? pipelineData.find(e => e.id === pipelineEvent) : null;

  // Open a competitor show in Market & Competitor Insights (year derived from show_id suffix)
  const openShowInsights = (show_id, ite_event_id) => {
    const m = String(show_id).match(/(\d{4})$/);
    setPendingInsights({ ite_event_id, show_id, year: m ? Number(m[1]) : null });
    nav("insights");
  };

  useEffect(() => {
    if (!pipelineEvent) return;
    setDetailLoading(true);
    setDetailData({ shows: [], team: [] });
    getPipelineDetail(pipelineEvent, { year: pipelineYear })
      .then(d => { setDetailData(d); setDetailLoading(false); })
      .catch(() => setDetailLoading(false));
  }, [pipelineEvent, pipelineYear]);

  useEffect(() => {
    if (!pipelineEvent || currentRole !== "Marketing Head") return;
    setMktDetailShows([]);
    getMarketingAssignments({ ite_event_id: pipelineEvent, year: pipelineYear })
      .then(data => { if (Array.isArray(data)) setMktDetailShows(data); })
      .catch(() => {});
  }, [pipelineEvent, currentRole, pipelineYear]);

  useEffect(() => {
    if (!pipelineEvent || currentRole !== "Sales Head") {
      setCrossEventLeads([]);
      return;
    }
    getCrossEventLeads(pipelineEvent, { year: pipelineYear })
      .then(data => { if (Array.isArray(data)) setCrossEventLeads(data); })
      .catch(() => {});
  }, [pipelineEvent, pipelineYear, currentRole]);

  const mktEventMap = {};
  mktAssignments.forEach(a => {
    if (!mktEventMap[a.ite_event_id]) {
      mktEventMap[a.ite_event_id] = { totalCompanies:0, pushed:0, needsEnrichment:0, reviewed:0, overdue:0, notStarted:0, inProgress:0, complete:0 };
    }
    const m = mktEventMap[a.ite_event_id];
    m.totalCompanies  += a.total_companies || 0;
    m.pushed          += a.pushed_count || 0;
    m.needsEnrichment += a.needs_enrichment_count || 0;
    m.reviewed        += a.reviewed_count || 0;
    if (a.is_overdue) m.overdue++;
    if (a.status === 'not_started')  m.notStarted++;
    else if (a.status === 'in_progress') m.inProgress++;
    else if (a.status === 'complete')    m.complete++;
  });
  const totalMktNeedsEnrichment = Object.values(mktEventMap).reduce((a, m) => a + m.needsEnrichment, 0);

  return (
    <PageShell>
      <style>{`*::-webkit-scrollbar{width:6px}*::-webkit-scrollbar-thumb{background:#d8dde3;border-radius:3px}*::-webkit-scrollbar-track{background:transparent}`}</style>

      <Sidebar active="pipeline" onNav={v => { setDrawerShow(null); nav(v); }}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Top bar */}
        <div style={{padding:"16px 28px",background:"#fff",borderBottom:"1px solid #F4F4F5",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,boxShadow:"0 1px 3px rgba(16,24,40,0.03)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {selEvent && (
              <button onClick={()=>{setPipelineEvent(null);setDrawerShow(null);}}
                style={{padding:"5px 12px",borderRadius:8,border:"1px solid #D8DADC",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46"}}
                onMouseEnter={e=>e.currentTarget.style.background="#F4F4F5"}
                onMouseLeave={e=>e.currentTarget.style.background="#fff"}>← Events</button>
            )}
            {selEvent && <span style={{color:"#AFABAB",fontSize:14}}>›</span>}
            <div>
              <div style={{fontSize:19,fontWeight:800,letterSpacing:-0.4,color:"#26262A"}}>
                {selEvent ? selEvent.name : isMH ? "Campaign Pipeline" : isSM ? "My Sales Reviews" : isMM ? "My Campaign Reviews" : "Sales Pipeline"}
              </div>
              <div style={{fontSize:11,color:"#AFABAB",marginTop:1}}>
                {selEvent
                  ? `${selEvent.sector} · Competitor show breakdown`
                  : isMH  ? "Your ITE events — campaign-push progress and reach at a glance."
                  : isSM  ? "Your assigned shows — click any to open its review."
                  : isMM  ? "Your assigned shows — click any to open its campaign review."
                  : "Track competitor show review progress, CRM readiness, and team coverage across your ITE event portfolio."}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {/* Year selector */}
            <FilterSelect value={pipelineYear} onChange={e => setPipelineYear(Number(e.target.value))} active={true}>
              {[2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </FilterSelect>
            <div style={{display:"flex",alignItems:"center",gap:7,padding:"5px 12px 5px 6px",background:"#F4F4F5",borderRadius:20,border:"1px solid #F4F4F5"}}>
              <div style={{width:26,height:26,borderRadius:"50%",background:isMH||isMM?"linear-gradient(135deg,#5F1156,#5F1156)":"linear-gradient(135deg,#E8431A,#E8431A)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff"}}>{isMH?"MH":isMM?"MM":isSM?"SM":"SH"}</div>
              <span style={{fontSize:11,fontWeight:600,color:"#3F3F46"}}>{currentRole}</span>
            </div>
          </div>
        </div>

        <div style={{flex:1,overflow:"auto",padding:"28px 32px 48px"}}>

          {pipelineError && (
            <div style={{padding:"12px 16px",background:"#fdecec",border:"1px solid #fbd5d5",borderRadius:8,color:"#C1121F",fontSize:12,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
              <AlertTriangle size={14} /> {pipelineError}
            </div>
          )}

          {/* ══ VIEW 1: EVENT LIST ═══════════════════════════════════════════ */}
          {!selEvent && (() => {
            const totalPendingEnrichment = pipelineData.reduce((a,e) => a + e.pendingEnrichment, 0);
            const totalAwaitingReview    = pipelineData.reduce((a,e) => a + e.awaitingReview,    0);
            const totalInReview          = pipelineData.reduce((a,e) => a + e.inReview,          0);
            const totalComplete          = pipelineData.reduce((a,e) => a + e.complete,          0);
            const totalCompaniesAll= pipelineData.reduce((a,e) => a + e.totalCompanies, 0);
            const totalCRMAll      = pipelineData.reduce((a,e) => a + e.crmApproved, 0);
            const totalPushedAll   = pipelineData.reduce((a,e) => a + e.crmPushed,   0);

            const filtered = eventSearch
              ? pipelineData.filter(e => `${e.name} ${e.sector}`.toLowerCase().includes(eventSearch.toLowerCase()))
              : pipelineData;
            const visibleEvents = filtered.filter(e => e.totalShows > 0).slice().sort((a,b) => b.totalShows - a.totalShows);
            const emptyEvents   = filtered.filter(e => e.totalShows === 0).slice().sort((a,b) => a.name.localeCompare(b.name));

            return (
            <>
              {/* ── KPI strip ──────────────────────────────────────────────── */}
              {isSH && pipelineData.length > 0 && (() => {
                const KpiCard = ({Icon, v, l, s, c, bg}) => (
                  <div style={{background:"#FAFAFA",borderRadius:10,border:"1px solid #F4F4F5",padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <div style={{width:30,height:30,borderRadius:8,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon size={13} color={c}/></div>
                      <div style={{fontSize:26,fontWeight:800,fontFamily:M,color:c,letterSpacing:-1.2,lineHeight:1}}>{v}</div>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:"#26262A"}}>{l}</div>
                    <div style={{fontSize:9,color:"#AFABAB",marginTop:1}}>{s}</div>
                  </div>
                );
                return (
                  <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:20}}>
                    {/* Show Review Progress */}
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Show Review Progress</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                        <KpiCard Icon={Sparkles}     v={totalPendingEnrichment} l="Pending Enrichment" s="Awaiting data"             c="#F18F18" bg="#fef4e6"/>
                        <KpiCard Icon={Clock}        v={totalAwaitingReview}    l="Awaiting Review"   s="Assigned, not started"    c="#E8431A" bg="#FCEBE5"/>
                        <KpiCard Icon={RefreshCw}    v={totalInReview}          l="In Review"         s="Sales review in progress" c="#5F1156" bg="#F5EEF2"/>
                        <KpiCard Icon={CheckCircle2} v={totalComplete}          l="Complete"          s="Sales review complete"    c="#16a34a" bg="#f0fdf4"/>
                      </div>
                    </div>
                    {/* Lead KPIs */}
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)",minWidth:360}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Lead KPIs</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                        <KpiCard Icon={Building2}    v={totalCompaniesAll} l="Total Leads" s="Across all shows"      c="#5F1156" bg="#F5EEF2"/>
                        <KpiCard Icon={CheckCircle2} v={totalCRMAll}       l="SQL"            s="Sales Qualified Lead"   c="#059669" bg="#ecfdf5"/>
                        <KpiCard Icon={Mail}         v={totalPushedAll}    l="MQL"            s="Marketing Qualified Lead" c="#960659" bg="#F6E6EF"/>
                      </div>
                    </div>
                  </div>
                );
              })()}


              {isMH && pipelineData.length > 0 && (() => {
                const totalMktPendingEnrichment = pipelineData.reduce((a,e) => a + e.pendingEnrichment,   0);
                const totalMktAwaiting          = pipelineData.reduce((a,e) => a + e.mktAwaitingReview,  0);
                const totalMktInProgress        = pipelineData.reduce((a,e) => a + e.mktInReview,        0);
                const totalMktComplete          = pipelineData.reduce((a,e) => a + e.mktComplete,        0);
                const totalMktCompanies  = Object.values(mktEventMap).reduce((a,m) => a + m.totalCompanies, 0);
                const totalMktPushed     = Object.values(mktEventMap).reduce((a,m) => a + m.pushed, 0);
                const KpiCard = ({Icon, v, l, s, c, bg}) => (
                  <div style={{background:"#FAFAFA",borderRadius:10,border:"1px solid #F4F4F5",padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <div style={{width:30,height:30,borderRadius:8,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon size={13} color={c}/></div>
                      <div style={{fontSize:26,fontWeight:800,fontFamily:M,color:c,letterSpacing:-1.2,lineHeight:1}}>{v}</div>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:"#26262A"}}>{l}</div>
                    <div style={{fontSize:9,color:"#AFABAB",marginTop:1}}>{s}</div>
                  </div>
                );
                return (
                  <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:20}}>
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Campaign Progress</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                        <KpiCard Icon={Sparkles}     v={totalMktPendingEnrichment} l="Pending Enrichment" s="Awaiting data"               c="#F18F18" bg="#fef4e6"/>
                        <KpiCard Icon={Clock}        v={totalMktAwaiting}         l="Awaiting Review"   s="Assigned, not started"      c="#E8431A" bg="#FCEBE5"/>
                        <KpiCard Icon={RefreshCw}    v={totalMktInProgress}       l="In Review"         s="Campaign review in progress" c="#5F1156" bg="#F5EEF2"/>
                        <KpiCard Icon={CheckCircle2} v={totalMktComplete}         l="Complete"          s="Campaign review complete"    c="#16a34a" bg="#f0fdf4"/>
                      </div>
                    </div>
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)",minWidth:360}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Lead KPIs</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                        <KpiCard Icon={Building2}    v={totalMktCompanies} l="Total Leads" s="Across all shows"       c="#5F1156" bg="#F5EEF2"/>
                        <KpiCard Icon={CheckCircle2} v={totalCRMAll}       l="SQL"            s="Sales Qualified Lead"    c="#059669" bg="#ecfdf5"/>
                        <KpiCard Icon={Mail}         v={totalMktPushed}    l="MQL"            s="Marketing Qualified Lead" c="#960659" bg="#F6E6EF"/>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Manager personal view (Sales Manager / Marketing Manager) ── */}
              {isMgr && (() => {
                const awaiting  = myAssignments.filter(a => a.status === "not_started").length;
                const inReview  = myAssignments.filter(a => a.status === "in_progress").length;
                const complete  = myAssignments.filter(a => a.status === "complete").length;
                const overdue   = myAssignments.filter(a => a.is_overdue).length;
                const totalComp = myAssignments.reduce((s,a) => s + (a.total_companies||0), 0);
                const fmtDate   = d => d ? new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—";
                const KpiCard   = ({Icon,v,l,s,c,bg}) => (
                  <div style={{background:"#FAFAFA",borderRadius:10,border:"1px solid #F4F4F5",padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <div style={{width:30,height:30,borderRadius:8,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon size={13} color={c}/></div>
                      <div style={{fontSize:26,fontWeight:800,fontFamily:M,color:c,letterSpacing:-1.2,lineHeight:1}}>{v}</div>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:"#26262A"}}>{l}</div>
                    <div style={{fontSize:9,color:"#AFABAB",marginTop:1}}>{s}</div>
                  </div>
                );
                const stageChip = (status, overdue, totalCompanies) => {
                  if (!overdue && status === "not_started" && totalCompanies === 0)
                                               return {label:"Pending Enrichment",c:"#9a5b00",bg:"#fef4e6",dot:"#F18F18"};
                  if (overdue)                 return {label:"Overdue",           c:"#9a5b00",bg:"#fef4e6",dot:"#F18F18"};
                  if (status==="in_progress")  return {label:"In Review",         c:"#5F1156",bg:"#F5EEF2",dot:"#5F1156"};
                  if (status==="complete")     return {label:"Complete",          c:"#15803d",bg:"#dcfce7",dot:"#16a34a"};
                  return                              {label:"Awaiting Review",   c:"#E8431A",bg:"#FCEBE5",dot:"#E8431A"};
                };
                return (<>
                  <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:20}}>
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>
                        {isSM ? "My Sales Reviews" : "My Campaign Reviews"}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                        <KpiCard Icon={Clock}         v={awaiting} l="Awaiting Review" s="Assigned, not started"                            c="#E8431A" bg="#FCEBE5"/>
                        <KpiCard Icon={RefreshCw}     v={inReview} l="In Review"       s={isSM?"Sales review in progress":"Campaign review in progress"} c="#5F1156" bg="#F5EEF2"/>
                        <KpiCard Icon={CheckCircle2}  v={complete} l="Complete"        s={isSM?"Sales review complete":"Campaign review complete"}        c="#16a34a" bg="#f0fdf4"/>
                        <KpiCard Icon={AlertTriangle} v={overdue}  l="Overdue"         s="Past deadline"                                    c="#C1121F" bg="#fdecec"/>
                      </div>
                    </div>
                    <div style={{background:"#fff",borderRadius:16,border:"1px solid #F4F4F5",padding:"20px 22px",boxShadow:"0 1px 3px rgba(16,24,40,0.04)",minWidth:280}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Lead KPIs</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
                        <KpiCard Icon={Building2} v={totalComp} l="Total Leads" s="Across my shows" c="#5F1156" bg="#F5EEF2"/>
                        {isSM && <KpiCard Icon={CheckCircle2} v={myAssignments.reduce((s,a)=>s+(a.highReady||0),0)}   l="SQL" s="Sales Qualified Lead"    c="#059669" bg="#ecfdf5"/>}
                        {!isSM && <KpiCard Icon={Mail}        v={myAssignments.reduce((s,a)=>s+(a.pushedCount||0),0)} l="MQL" s="Marketing Qualified Lead" c="#960659" bg="#F6E6EF"/>}
                      </div>
                    </div>
                  </div>

                  {myAssignments.length === 0 ? (
                    <div style={{textAlign:"center",padding:"60px 0",color:"#AFABAB"}}>
                      <div style={{marginBottom:8,display:"flex",justifyContent:"center"}}><ClipboardList size={32} color="#AFABAB"/></div>
                      <div style={{fontSize:14,fontWeight:600,color:"#818084"}}>No shows assigned yet</div>
                      <div style={{fontSize:11,marginTop:4}}>Your assigned shows will appear here.</div>
                    </div>
                  ) : (
                    <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",overflow:"hidden",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:F,tableLayout:"fixed"}}>
                        <colgroup>
                          <col style={{width:"32%"}}/><col style={{width:"18%"}}/><col style={{width:"16%"}}/>
                          <col style={{width:"10%"}}/><col style={{width:"16%"}}/><col style={{width:"8%"}}/>
                        </colgroup>
                        <thead>
                          <tr>
                            {["Competitor Show","ITE Event","Status","Companies",isSM?"Review Progress":"Push Progress","Deadline"].map((h,i)=>(
                              <th key={i} style={{padding:"10px 14px",fontSize:9,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.5,background:"#FAFAFA",borderBottom:"1px solid #F4F4F5",textAlign:i>=3?"center":"left",whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {myAssignments.map((a,i) => {
                            const chip   = stageChip(a.status, a.is_overdue, a.total_companies||0);
                            const done   = isSM ? (a.reviewed_count||0) : (a.pushed_count||0);
                            const total  = a.total_companies||0;
                            const pct    = total>0 ? Math.round(done/total*100) : 0;
                            const event  = pipelineData.find(e=>e.id===a.ite_event_id);
                            return (
                              <tr key={a.show_id}
                                style={{cursor:"pointer"}}
                                onClick={()=>openShowInsights(a.show_id, a.ite_event_id)}
                                onMouseEnter={e=>{Array.from(e.currentTarget.cells).forEach(c=>c.style.background="#FAFAFA");}}
                                onMouseLeave={e=>{Array.from(e.currentTarget.cells).forEach(c=>c.style.background="");}}
                              >
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle"}}>
                                  <div style={{fontWeight:700,fontSize:13,color:"#26262A"}}>{a.show_name}</div>
                                </td>
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle"}}>
                                  <div style={{fontSize:11,color:"#818084",fontWeight:500}}>{event?.name||a.ite_event_id}</div>
                                </td>
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle"}}>
                                  <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,background:chip.bg,fontSize:10,fontWeight:700,color:chip.c,whiteSpace:"nowrap"}}>
                                    <span style={{width:6,height:6,borderRadius:"50%",background:chip.dot,flexShrink:0}}/>{chip.label}
                                  </span>
                                </td>
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle",textAlign:"center",fontWeight:800,fontSize:15,fontFamily:M,color:"#3F3F46"}}>{total}</td>
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle",paddingRight:18}}>
                                  {total>0 ? (<>
                                    <div style={{fontSize:10,fontWeight:700,color:pct>=60?"#16a34a":pct>0?"#5F1156":"#AFABAB",marginBottom:3}}>{pct}%</div>
                                    <PBar pct={pct}/>
                                    <div style={{fontSize:8.5,color:"#AFABAB",marginTop:3}}>{done} / {total}</div>
                                  </>) : <span style={{fontSize:9,color:"#AFABAB"}}>—</span>}
                                </td>
                                <td style={{padding:"14px 14px",borderBottom:i<myAssignments.length-1?"1px solid #F4F4F5":"none",verticalAlign:"middle",textAlign:"center",fontSize:10,color:a.is_overdue?"#C1121F":"#818084",fontWeight:a.is_overdue?700:400}}>{fmtDate(a.deadline)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>);
              })()}

              {pipelineData.length === 0 && isSH && (
                <div style={{textAlign:"center",padding:"60px 0",color:"#AFABAB"}}>
                  <div style={{marginBottom:8,display:"flex",justifyContent:"center"}}><ClipboardList size={32} color="#AFABAB"/></div>
                  <div style={{fontSize:14,fontWeight:600,color:"#818084"}}>No pipeline data yet</div>
                  <div style={{fontSize:11,marginTop:4}}>Competitor show assignments will appear here once created.</div>
                </div>
              )}

              {visibleEvents.length === 0 && pipelineData.length > 0 && (
                <div style={{textAlign:"center",padding:"40px 0",color:"#AFABAB",fontSize:12}}>
                  No events match "<strong style={{color:"#818084"}}>{eventSearch}</strong>"
                </div>
              )}

              {/* ── Event table (Sales Head) ────────────────────────────────── */}
              {isSH && visibleEvents.length > 0 && (
                <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",overflow:"hidden",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                  <style>{`
                    .pl-th { padding:10px 14px; font-size:9px; font-weight:700; color:#AFABAB; text-transform:uppercase; letter-spacing:0.5px; background:#FAFAFA; border-bottom:1px solid #F4F4F5; white-space:nowrap; }
                    .pl-tr:hover td { background:#FAFAFA; }
                    .pl-tr td { padding:14px 14px; border-bottom:1px solid #F4F4F5; vertical-align:middle; }
                    .pl-tr:last-child td { border-bottom:none; }
                  `}</style>
                  <table style={{width:"100%",borderCollapse:"collapse",fontFamily:F,tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:"26%"}}/>
                      <col style={{width:"8%"}}/>
                      <col style={{width:"24%"}}/>
                      <col style={{width:"42%"}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="pl-th" style={{textAlign:"left"}}>ITE Event</th>
                        <th className="pl-th" style={{textAlign:"center"}}>Shows</th>
                        <th className="pl-th" style={{textAlign:"center"}}>Leads</th>
                        <th className="pl-th" style={{textAlign:"left"}}>Shows Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEvents.map(ev => {
                        const chips = [
                          ev.pendingEnrichment > 0 && {label:`${ev.pendingEnrichment} Pending Enrichment`, dot:"#F18F18", bg:"#fef4e6", c:"#9a5b00"},
                          ev.awaitingReview    > 0 && {label:`${ev.awaitingReview} Awaiting Review`,       dot:"#E8431A", bg:"#FCEBE5", c:"#E8431A"},
                          ev.inReview          > 0 && {label:`${ev.inReview} In Review`,                   dot:"#5F1156", bg:"#F5EEF2", c:"#5F1156"},
                          ev.complete          > 0 && {label:`${ev.complete} Complete`,                    dot:"#16a34a", bg:"#dcfce7", c:"#15803d"},
                        ].filter(Boolean);
                        return (
                          <tr key={ev.id} className="pl-tr" style={{cursor:"pointer"}}
                            onClick={()=>{setPipelineEvent(ev.id);setEventSearch("");setDrawerShow(null);}}>
                            <td>
                              <div style={{fontWeight:700,fontSize:13,color:"#26262A"}}>{ev.name}</div>
                              <div style={{fontSize:9.5,color:"#AFABAB",marginTop:2}}>{ev.sector}</div>
                            </td>
                            <td style={{textAlign:"center",fontWeight:800,fontSize:17,fontFamily:M,color:"#3F3F46"}}>{ev.totalShows}</td>
                            <td style={{textAlign:"center"}}>
                              <div style={{fontWeight:800,fontSize:15,fontFamily:M,color:"#26262A"}}>{ev.totalCompanies.toLocaleString()}</div>
                              {(ev.crmApproved > 0 || ev.crmPushed > 0) && (
                                <div style={{display:"flex",gap:5,justifyContent:"center",alignItems:"center",marginTop:3}}>
                                  {ev.crmApproved > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#16a34a",whiteSpace:"nowrap"}}>{ev.crmApproved} SQL</span>}
                                  {ev.crmApproved > 0 && ev.crmPushed > 0 && <span style={{color:"#AFABAB",fontSize:9}}>|</span>}
                                  {ev.crmPushed   > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#960659",whiteSpace:"nowrap"}}>{ev.crmPushed} MQL</span>}
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {chips.length > 0 ? chips.map((ch,i) => (
                                  <span key={i} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,background:ch.bg,fontSize:10,fontWeight:700,color:ch.c,whiteSpace:"nowrap"}}>
                                    <span style={{width:6,height:6,borderRadius:"50%",background:ch.dot,flexShrink:0}}/>
                                    {ch.label}
                                  </span>
                                )) : (
                                  <span style={{fontSize:10,color:"#AFABAB"}}>No activity yet</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {emptyEvents.map(ev => (
                        <tr key={ev.id} style={{opacity:0.35}}>
                          <td style={{padding:"10px 14px"}}>
                            <div style={{fontWeight:600,fontSize:12,color:"#818084"}}>{ev.name}</div>
                            <div style={{fontSize:9,color:"#818084"}}>{ev.sector}</div>
                          </td>
                          <td colSpan={3} style={{padding:"10px 14px",fontSize:10,color:"#818084",fontStyle:"italic"}}>No shows tracked yet</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Marketing Head table (matches Sales Head layout) ────────── */}
              {isMH && visibleEvents.length > 0 && (
                <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",overflow:"hidden",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                  <style>{`
                    .pl-th { padding:10px 14px; font-size:9px; font-weight:700; color:#AFABAB; text-transform:uppercase; letter-spacing:0.5px; background:#FAFAFA; border-bottom:1px solid #F4F4F5; white-space:nowrap; }
                    .pl-tr:hover td { background:#FAFAFA; }
                    .pl-tr td { padding:14px 14px; border-bottom:1px solid #F4F4F5; vertical-align:middle; }
                    .pl-tr:last-child td { border-bottom:none; }
                  `}</style>
                  <table style={{width:"100%",borderCollapse:"collapse",fontFamily:F,tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:"26%"}}/>
                      <col style={{width:"8%"}}/>
                      <col style={{width:"24%"}}/>
                      <col style={{width:"42%"}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="pl-th" style={{textAlign:"left"}}>ITE Event</th>
                        <th className="pl-th" style={{textAlign:"center"}}>Shows</th>
                        <th className="pl-th" style={{textAlign:"center"}}>Leads</th>
                        <th className="pl-th" style={{textAlign:"left"}}>Shows Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEvents.map(ev => {
                        const mktEv = mktEventMap[ev.id] || {};
                        const chips = [
                          ev.pendingEnrichment  > 0 && {label:`${ev.pendingEnrichment} Pending Enrichment`,   dot:"#F18F18", bg:"#fef4e6", c:"#9a5b00"},
                          ev.mktAwaitingReview  > 0 && {label:`${ev.mktAwaitingReview} Awaiting Review`,      dot:"#E8431A", bg:"#FCEBE5", c:"#E8431A"},
                          ev.mktInReview        > 0 && {label:`${ev.mktInReview} In Review`,                  dot:"#5F1156", bg:"#F5EEF2", c:"#5F1156"},
                          ev.mktComplete        > 0 && {label:`${ev.mktComplete} Complete`,                   dot:"#16a34a", bg:"#dcfce7", c:"#15803d"},
                        ].filter(Boolean);
                        return (
                          <tr key={ev.id} className="pl-tr" style={{cursor:"pointer"}}
                            onClick={()=>{setPipelineEvent(ev.id);setEventSearch("");setDrawerShow(null);}}>
                            <td>
                              <div style={{fontWeight:700,fontSize:13,color:"#26262A"}}>{ev.name}</div>
                              <div style={{fontSize:9.5,color:"#AFABAB",marginTop:2}}>{ev.sector}</div>
                            </td>
                            <td style={{textAlign:"center",fontWeight:800,fontSize:17,fontFamily:M,color:"#3F3F46"}}>{ev.totalShows}</td>
                            <td style={{textAlign:"center"}}>
                              <div style={{fontWeight:800,fontSize:15,fontFamily:M,color:"#26262A"}}>{(mktEv.totalCompanies||0).toLocaleString()}</div>
                              {(ev.crmApproved > 0 || (mktEv.pushed||0) > 0) && (
                                <div style={{display:"flex",gap:5,justifyContent:"center",alignItems:"center",marginTop:3}}>
                                  {ev.crmApproved > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#16a34a",whiteSpace:"nowrap"}}>{ev.crmApproved} SQL</span>}
                                  {ev.crmApproved > 0 && (mktEv.pushed||0) > 0 && <span style={{color:"#AFABAB",fontSize:9}}>|</span>}
                                  {(mktEv.pushed||0) > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#960659",whiteSpace:"nowrap"}}>{mktEv.pushed} MQL</span>}
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {chips.length > 0 ? chips.map((ch,i) => (
                                  <span key={i} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,background:ch.bg,fontSize:10,fontWeight:700,color:ch.c,whiteSpace:"nowrap"}}>
                                    <span style={{width:6,height:6,borderRadius:"50%",background:ch.dot,flexShrink:0}}/>
                                    {ch.label}
                                  </span>
                                )) : (
                                  <span style={{fontSize:10,color:"#AFABAB"}}>No campaigns yet</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {emptyEvents.map(ev => (
                        <tr key={ev.id} style={{opacity:0.35}}>
                          <td style={{padding:"10px 14px"}}>
                            <div style={{fontWeight:600,fontSize:12,color:"#818084"}}>{ev.name}</div>
                            <div style={{fontSize:9,color:"#818084"}}>{ev.sector}</div>
                          </td>
                          <td colSpan={3} style={{padding:"10px 14px",fontSize:10,color:"#818084",fontStyle:"italic"}}>No shows tracked yet</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>);
          })()}

          {/* ══ VIEW 2: EVENT DETAIL ═════════════════════════════════════════ */}
          {selEvent && (() => {
            if (isMH) {
              // Use getPipelineDetail shows as base (always populated); overlay marketing metrics
              const shows = detailData.shows || [];
              const mktByShow = {};
              mktDetailShows.forEach(m => { mktByShow[m.show_id] = m; });

              const totalPushed      = mktDetailShows.reduce((a,s) => a + (s.pushed_count||0), 0);
              const totalNeedsEnrich = mktDetailShows.reduce((a,s) => a + (s.needs_enrichment_count||0), 0);
              const totalReviewed    = shows.reduce((a,s) => a + (s.reviewed_count||0), 0);
              const totalComps       = selEvent.totalCompanies || 0;
              const isMktPendingEnrich = s => ["awaiting_upload","enriching"].includes(s.enrich_status) || !s.enrich_status;
              const mktPendingN    = shows.filter(s => isMktPendingEnrich(s)).length;
              const mktAwaitingN   = shows.filter(s => !isMktPendingEnrich(s) && (s.mkt_status === "not_started" || !s.mkt_status)).length;
              const mktInCampaignN = shows.filter(s => s.mkt_status === "in_progress").length;
              const mktCompleteN   = shows.filter(s => s.mkt_status === "complete").length;
              const MKpiCard = ({Icon, v, l, s, c, bg}) => (
                <div style={{background:"#FAFAFA",borderRadius:10,border:"1px solid #F4F4F5",padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <div style={{width:30,height:30,borderRadius:8,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{Icon && <Icon size={13} color={c}/>}</div>
                    <div style={{fontSize:26,fontWeight:800,fontFamily:M,color:c,letterSpacing:-1.2,lineHeight:1}}>{v}</div>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:"#26262A"}}>{l}</div>
                  <div style={{fontSize:9,color:"#AFABAB",marginTop:1}}>{s}</div>
                </div>
              );
              return (<>
                {/* MH KPI strip — two grouped panels matching Sales Head */}
                <div style={{display:"flex",gap:12,marginBottom:18}}>
                  <div style={{flex:3,background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"16px 20px",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                    <div style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.7,marginBottom:12}}>Campaign Progress</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                      <MKpiCard Icon={Sparkles}     v={mktPendingN}    l="Pending Enrichment" s="Awaiting data"               c="#F18F18" bg="#fef4e6"/>
                      <MKpiCard Icon={Clock}        v={mktAwaitingN}   l="Awaiting Review"    s="Assigned, not started"       c="#E8431A" bg="#FCEBE5"/>
                      <MKpiCard Icon={RefreshCw}    v={mktInCampaignN} l="In Review"          s="Campaign review in progress" c="#5F1156" bg="#F5EEF2"/>
                      <MKpiCard Icon={CheckCircle2} v={mktCompleteN}   l="Complete"           s="Campaign review complete"    c="#16a34a" bg="#f0fdf4"/>
                    </div>
                  </div>
                  <div style={{flex:2,background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"16px 20px",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                    <div style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.7,marginBottom:12}}>Lead KPIs</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                      <MKpiCard Icon={Building2}    v={totalComps}          l="Total Leads" s="Across all shows"       c="#5F1156" bg="#F5EEF2"/>
                      <MKpiCard Icon={CheckCircle2} v={selEvent.crmApproved||0} l="SQL"         s="Sales Qualified Lead"   c="#059669" bg="#ecfdf5"/>
                      <MKpiCard Icon={Mail}         v={totalPushed}         l="MQL"             s="Marketing Qualified Lead" c="#960659" bg="#F6E6EF"/>
                    </div>
                  </div>
                </div>
                {/* Campaign Coverage */}
                <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"18px 20px",marginBottom:16,boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#26262A",marginBottom:14}}>Competitor Show Coverage</div>
                  {detailLoading ? (
                    <div style={{textAlign:"center",padding:"32px 0",color:"#AFABAB",fontSize:12}}>Loading shows…</div>
                  ) : shows.length === 0 ? (
                    <div style={{textAlign:"center",padding:"32px 0",color:"#AFABAB",fontSize:12}}>No show data available for this event.</div>
                  ) : (
                    <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                      <colgroup>
                        <col style={{width:"30%"}}/><col style={{width:"11%"}}/><col style={{width:"19%"}}/><col style={{width:"16%"}}/><col style={{width:"24%"}}/>
                      </colgroup>
                      <thead>
                        <tr>
                          {[["Competitor Show","left"],["Leads","center"],["Owner","center"],["Deadline","center"],["Review Progress","center"]].map(([h,a],i) => (
                            <th key={i} style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.4,padding:"0 8px 10px",borderBottom:"1px solid #F4F4F5",textAlign:a}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {shows.map((s,i) => {
                          const mkt = mktByShow[s.show_id] || {};
                          const pushedCt = mkt.pushed_count || 0;
                          const reviewedCt = mkt.reviewed_count || 0;
                          const mktOwner = mkt.owner_name || null;
                          const mktDeadline = mkt.deadline || null;
                          const pct = s.total_companies > 0 ? Math.round(reviewedCt/s.total_companies*100) : 0;
                          const ini = mktOwner ? mktOwner[0].toUpperCase() : "?";
                          const fmtD = d => new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
                          const isOverdue = mktDeadline && new Date(mktDeadline) < new Date() && mkt.status !== "complete";
                          return (
                            <tr key={s.show_id} style={{borderBottom:i<shows.length-1?"1px solid #F4F4F5":"none"}}>
                              <td style={{padding:"14px 8px",verticalAlign:"middle"}}>
                                <div style={{display:"flex",alignItems:"center",gap:10}}>
                                  <div style={{width:34,height:34,borderRadius:8,background:"#F5EEF2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"#5F1156",flexShrink:0,fontFamily:M}}>{s.show_name.slice(0,3).toUpperCase()}</div>
                                  <div>
                                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                                      <div
                                        onClick={() => openShowInsights(s.show_id, selEvent?.id)}
                                        style={{fontSize:12,fontWeight:700,color:"#E8431A",cursor:"pointer"}}
                                        onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                                        onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                                        {s.show_name}
                                      </div>
                                      <span
                                        title="Campaign Review"
                                        onClick={() => { setCurrentRole("Marketing Manager"); setPendingLeadsShow({show_id:s.show_id,tab:"campaignlists"}); nav("leads"); }}
                                        style={{display:"flex",alignItems:"center",justifyContent:"center",width:22,height:22,borderRadius:5,background:"#FCEBE5",border:"1px solid #F7C9B8",cursor:"pointer",flexShrink:0}}>
                                        <ClipboardList size={11} color="#E8431A"/>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                <div style={{fontSize:15,fontWeight:800,fontFamily:M,color:"#3F3F46",textAlign:"center"}}>{s.total_companies??0}</div>
                                {((s.approved_count||0) > 0 || pushedCt > 0) && (
                                  <div style={{display:"flex",gap:5,alignItems:"center",justifyContent:"center",marginTop:3}}>
                                    {(s.approved_count||0) > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#16a34a",whiteSpace:"nowrap"}}>{s.approved_count} SQL</span>}
                                    {(s.approved_count||0) > 0 && pushedCt > 0 && <span style={{color:"#AFABAB",fontSize:9}}>|</span>}
                                    {pushedCt > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#960659",whiteSpace:"nowrap"}}>{pushedCt} MQL</span>}
                                  </div>
                                )}
                              </td>
                              <td style={{padding:"14px 8px",verticalAlign:"middle"}}>
                                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                                  {!mktOwner ? (
                                    <button
                                      onClick={e => { e.stopPropagation(); setAssignModal({show_id:s.show_id,show_name:s.show_name}); setAssignError(""); }}
                                      style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:6,border:"1.5px dashed #d8b4fe",background:"#F5EEF2",color:"#5F1156",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,transition:"all 0.15s"}}
                                      onMouseEnter={e=>{e.currentTarget.style.background="#f3e8ff";e.currentTarget.style.borderStyle="solid";}}
                                      onMouseLeave={e=>{e.currentTarget.style.background="#F5EEF2";e.currentTarget.style.borderStyle="dashed";}}>
                                      <span style={{fontSize:12}}>+</span> Assign
                                    </button>
                                  ) : (<>
                                    <div style={{width:24,height:24,borderRadius:"50%",background:"#F5EEF2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:"#5F1156",flexShrink:0}}>{ini}</div>
                                    <span style={{fontSize:10.5,fontWeight:500,color:"#3F3F46"}}>{mktOwner}</span>
                                  </>)}
                                </div>
                              </td>
                              <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                {mktDeadline
                                  ? <span style={{fontSize:10.5,fontWeight:500,color:isOverdue?"#C1121F":"#3F3F46"}}>{fmtD(mktDeadline)}</span>
                                  : <span style={{fontSize:9,color:"#AFABAB"}}>—</span>}
                              </td>
                              <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                {s.total_companies > 0 ? (<>
                                  <div style={{fontSize:10,fontWeight:700,color:pct>=60?"#16a34a":pct>0?"#5F1156":"#AFABAB",marginBottom:4,textAlign:"center"}}>{pct}%</div>
                                  <div style={{maxWidth:160,margin:"0 auto"}}><PBar pct={pct}/></div>
                                  <div style={{fontSize:8.5,color:"#AFABAB",marginTop:3,textAlign:"center"}}>{reviewedCt} / {s.total_companies} reviewed</div>
                                </>) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                {/* MH Team Workload */}
                {(() => {
                  const fmtD = d => new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
                  const mktTeamMap = {};
                  mktDetailShows.forEach(m => {
                    if (!m.owner_name) return;
                    const k = m.owner_name;
                    if (!mktTeamMap[k]) mktTeamMap[k] = {name:k,assigned_shows:0,pushed_count:0,total_companies:0,nearest_deadline:null,earliest_assignment:null};
                    mktTeamMap[k].assigned_shows += 1;
                    mktTeamMap[k].pushed_count += (m.pushed_count||0);
                    mktTeamMap[k].total_companies += (m.total_companies||0);
                    if (m.deadline && (!mktTeamMap[k].nearest_deadline || m.deadline < mktTeamMap[k].nearest_deadline)) mktTeamMap[k].nearest_deadline = m.deadline;
                    if (m.created_at && (!mktTeamMap[k].earliest_assignment || m.created_at < mktTeamMap[k].earliest_assignment)) mktTeamMap[k].earliest_assignment = m.created_at;
                  });
                  const mktTeam = Object.values(mktTeamMap).sort((a,b)=>a.name.localeCompare(b.name));
                  return (
                    <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"18px 20px",marginBottom:16,boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:14,fontWeight:800,color:"#26262A"}}>Team Workload</div>
                      </div>
                      {mktTeam.length === 0 ? (
                        <div style={{textAlign:"center",padding:"24px 0",color:"#AFABAB",fontSize:12}}>No assignments yet — all shows are unassigned.</div>
                      ) : (
                        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                          <colgroup>
                            <col style={{width:"34%"}}/><col style={{width:"13%"}}/><col style={{width:"23%"}}/><col style={{width:"30%"}}/>
                          </colgroup>
                          <thead>
                            <tr>
                              {[["Marketing Manager","left"],["Shows Assigned","center"],["Leads Reviewed","center"],["Nearest Deadline","center"]].map(([h,a],i) => (
                                <th key={i} style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.4,padding:"0 8px 10px",borderBottom:"1px solid #F4F4F5",textAlign:a}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {mktTeam.map((m,i) => {
                              const deadline = m.nearest_deadline
                                ? fmtD(m.nearest_deadline)
                                : m.earliest_assignment
                                  ? fmtD(new Date(m.earliest_assignment).getTime() + 14*24*60*60*1000)
                                  : "—";
                              return (
                                <tr key={i} style={{borderBottom:i<mktTeam.length-1?"1px solid #F4F4F5":"none"}}>
                                  <td style={{padding:"13px 8px",verticalAlign:"middle"}}>
                                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                                      <div style={{width:28,height:28,borderRadius:"50%",background:"#F5EEF2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#5F1156"}}>{m.name[0]}</div>
                                      <div style={{fontSize:11.5,fontWeight:600,color:"#26262A"}}>{m.name}</div>
                                    </div>
                                  </td>
                                  <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                    <div style={{fontSize:13,fontWeight:700,fontFamily:M,color:"#3F3F46",textAlign:"center"}}>{m.assigned_shows}</div>
                                  </td>
                                  <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                    <div style={{fontSize:13,fontWeight:700,fontFamily:M,color:"#3F3F46",textAlign:"center"}}>{m.total_companies}</div>
                                    {m.pushed_count > 0 && (
                                      <div style={{display:"flex",gap:5,alignItems:"center",justifyContent:"center",marginTop:3}}>
                                        <span style={{fontSize:9.5,fontWeight:600,color:"#960659",whiteSpace:"nowrap"}}>{m.pushed_count} MQL</span>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                                    <div style={{fontSize:11,color:"#818084",textAlign:"center"}}>{deadline}</div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })()}
              </>);
            }

            const shows = detailData.shows || [];
            const team  = detailData.team  || [];

            const mapStatus = (s, overdue) =>
              !s                    ? "Unassigned"
              : overdue             ? "Behind"
              : s === "in_progress" ? "In Progress"
              : s === "complete"    ? "Done"
              : s === "not_started" ? "Not Started"
              : "Not Started";

            const isPendingEnrich = s => ["awaiting_upload","enriching"].includes(s.enrich_status) || !s.enrich_status;
            const pendingN    = shows.filter(s => isPendingEnrich(s)).length;
            const awaitingN   = shows.filter(s => !isPendingEnrich(s) && (s.assignment_status === "not_started" || !s.assignment_status)).length;
            const inReviewN   = shows.filter(s => s.assignment_status === "in_progress").length;
            const completedN  = shows.filter(s => s.assignment_status === "complete").length;
            const totalCRM    = shows.reduce((a, s) => a + (s.approved_count || 0), 0);

            const KpiCard = ({Icon, v, l, s, c, bg}) => (
              <div style={{background:"#FAFAFA",borderRadius:10,border:"1px solid #F4F4F5",padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <div style={{width:30,height:30,borderRadius:8,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{Icon && <Icon size={13} color={c}/>}</div>
                  <div style={{fontSize:26,fontWeight:800,fontFamily:M,color:c,letterSpacing:-1.2,lineHeight:1}}>{v}</div>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:"#26262A"}}>{l}</div>
                <div style={{fontSize:9,color:"#AFABAB",marginTop:1}}>{s}</div>
              </div>
            );

            return (
            <>
              {/* KPI strip — two grouped sections */}
              <div style={{display:"flex",gap:12,marginBottom:18}}>
                {/* Show Review Progress — 4 stages */}
                <div style={{flex:3,background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"16px 20px",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                  <div style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.7,marginBottom:12}}>Show Review Progress</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                    <KpiCard Icon={Sparkles}     v={pendingN}   l="Pending Enrichment" s="Awaiting data"             c="#F18F18" bg="#fef4e6"/>
                    <KpiCard Icon={Clock}        v={awaitingN}  l="Awaiting Review"    s="Assigned, not started"     c="#E8431A" bg="#FCEBE5"/>
                    <KpiCard Icon={RefreshCw}    v={inReviewN}  l="In Review"          s="Sales review in progress"  c="#5F1156" bg="#F5EEF2"/>
                    <KpiCard Icon={CheckCircle2} v={completedN} l="Complete"           s="Sales review complete"     c="#16a34a" bg="#f0fdf4"/>
                  </div>
                </div>
                {/* Lead KPIs */}
                <div style={{flex:3,background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"16px 20px",boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                  <div style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.7,marginBottom:12}}>Lead KPIs</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                    <KpiCard Icon={Building2}    v={selEvent.totalCompanies} l="Total Leads" s="Year-filtered"          c="#5F1156" bg="#F5EEF2"/>
                    <KpiCard Icon={CheckCircle2} v={totalCRM}                l="SQL"             s="Sales Qualified Lead"   c="#16a34a" bg="#f0fdf4"/>
                    <KpiCard Icon={Mail}         v={selEvent.crmPushed}      l="MQL"             s="Marketing Qualified Lead" c="#960659" bg="#F6E6EF"/>
                  </div>
                </div>
              </div>

              {/* Competitor Show Coverage table */}
              <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"18px 20px",marginBottom:16,boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#26262A"}}>Competitor Show Coverage</div>
                  {isSH && shows.length > 0 && (
                    <Pencil size={14} color="#E8431A" style={{cursor:"pointer",flexShrink:0}}
                      title="Edit assignment"
                      onClick={() => { setAssignModal({show_id: shows[0].show_id, show_name: shows[0].show_name, isEdit: shows[0].owner_id != null, showPicker: true}); setAssignForm({show_id: shows[0].show_id, owner_id: shows[0].owner_id||"", review_type: shows[0].review_type||"online_review", deadline: shows[0].deadline||""}); setAssignError(""); }}
                    />
                  )}
                </div>
                {detailLoading ? (
                  <div style={{textAlign:"center",padding:"32px 0",color:"#AFABAB",fontSize:12}}>Loading shows…</div>
                ) : shows.length === 0 ? (
                  <div style={{textAlign:"center",padding:"32px 0",color:"#AFABAB",fontSize:12}}>No show data available for this event.</div>
                ) : (
                  <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:"30%"}}/><col style={{width:"11%"}}/><col style={{width:"19%"}}/><col style={{width:"16%"}}/><col style={{width:"24%"}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        {[["Competitor Show","left"],["Leads","center"],["Owner","center"],["Deadline","center"],["Review Progress","center"]].map(([h,a],i) => (
                          <th key={i} style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.4,padding:"0 8px 10px",borderBottom:"1px solid #F4F4F5",textAlign:a}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shows.map((s, i) => {
                        const reviewed = s.reviewed_count || 0;
                        const total    = s.total_companies || 0;
                        const pct      = total > 0 ? Math.round(reviewed / total * 100) : 0;
                        const isUnassigned = !s.assignment_status;
                        const ini = s.owner_name ? s.owner_name[0].toUpperCase() : "?";
                        const fmtD = d => new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
                        const isOverdue = s.deadline && new Date(s.deadline) < new Date() && s.assignment_status !== "complete";
                        return (
                          <tr key={s.show_id} style={{borderBottom:i<shows.length-1?"1px solid #F4F4F5":"none"}}>
                            <td style={{padding:"14px 8px",verticalAlign:"middle"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div
                                  onClick={() => openShowInsights(s.show_id, selEvent?.id)}
                                  style={{fontSize:12,fontWeight:700,color:"#E8431A",cursor:"pointer"}}
                                  onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                                  onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                                  {s.show_name}
                                </div>
                                <span
                                  title="Sales Review"
                                  onClick={() => { setPendingLeadsShow({show_id:s.show_id,tab:"mylists"}); nav("leads"); }}
                                  style={{display:"flex",alignItems:"center",justifyContent:"center",width:22,height:22,borderRadius:5,background:"#FCEBE5",border:"1px solid #F7C9B8",cursor:"pointer",flexShrink:0}}>
                                  <ClipboardList size={11} color="#E8431A"/>
                                </span>
                              </div>
                            </td>
                            <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              <div style={{fontSize:15,fontWeight:800,fontFamily:M,color:"#3F3F46"}}>{total}</div>
                              {((s.approved_count||0) > 0 || (s.pushed_count||0) > 0) && (
                                <div style={{display:"flex",gap:5,alignItems:"center",justifyContent:"center",marginTop:3}}>
                                  {(s.approved_count||0) > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#16a34a",whiteSpace:"nowrap"}}>{s.approved_count} SQL</span>}
                                  {(s.approved_count||0) > 0 && (s.pushed_count||0) > 0 && <span style={{color:"#AFABAB",fontSize:9}}>|</span>}
                                  {(s.pushed_count||0) > 0 && <span style={{fontSize:9.5,fontWeight:600,color:"#960659",whiteSpace:"nowrap"}}>{s.pushed_count} MQL</span>}
                                </div>
                              )}
                            </td>
                            <td style={{padding:"14px 8px",verticalAlign:"middle"}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                                {isUnassigned ? (
                                  <button
                                    onClick={e => { e.stopPropagation(); setAssignModal({show_id:s.show_id,show_name:s.show_name}); setAssignError(""); }}
                                    style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:6,border:"1.5px dashed #E39B98",background:"#fdecec",color:"#C1121F",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,transition:"all 0.15s"}}
                                    onMouseEnter={e=>{e.currentTarget.style.background="#fdecec";e.currentTarget.style.borderStyle="solid";}}
                                    onMouseLeave={e=>{e.currentTarget.style.background="#fdecec";e.currentTarget.style.borderStyle="dashed";}}>
                                    <span style={{fontSize:12}}>+</span> Assign
                                  </button>
                                ) : (
                                  <>
                                    <div style={{width:24,height:24,borderRadius:"50%",background:"#FCEBE5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:"#E8431A",flexShrink:0}}>{ini}</div>
                                    <span style={{fontSize:10.5,fontWeight:500,color:"#3F3F46"}}>{s.owner_name}</span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              {s.deadline
                                ? <span style={{fontSize:10.5,fontWeight:500,color:isOverdue?"#C1121F":"#3F3F46"}}>{fmtD(s.deadline)}</span>
                                : <span style={{fontSize:9,color:"#AFABAB"}}>—</span>}
                            </td>
                            <td style={{padding:"14px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              {total > 0 ? (
                                <>
                                  <div style={{fontSize:10,fontWeight:700,color:pct>=100?"#16a34a":pct>0?"#9a5b00":"#AFABAB",marginBottom:4}}>{pct}%</div>
                                  <div style={{maxWidth:160,margin:"0 auto"}}><PBar pct={pct}/></div>
                                  <div style={{fontSize:8.5,color:"#AFABAB",marginTop:3}}>{reviewed} / {total} reviewed</div>
                                </>
                              ) : <span style={{fontSize:9,color:"#AFABAB"}}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>


              {/* Team Workload */}
              <div style={{background:"#fff",borderRadius:14,border:"1px solid #F4F4F5",padding:"18px 20px",marginBottom:16,boxShadow:"0 1px 2px rgba(16,24,40,0.04)"}}>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#26262A"}}>Team Workload</div>
                </div>

                {team.length === 0 ? (
                  <div style={{textAlign:"center",padding:"24px 0",color:"#AFABAB",fontSize:12}}>No assignments yet — all shows are unassigned.</div>
                ) : (
                  <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:"34%"}}/><col style={{width:"13%"}}/><col style={{width:"23%"}}/><col style={{width:"30%"}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        {[["Sales Manager","left"],["Shows Assigned","center"],["Leads Reviewed","center"],["Nearest Deadline","center"]].map(([h,a],i) => (
                          <th key={i} style={{fontSize:8.5,fontWeight:700,color:"#AFABAB",textTransform:"uppercase",letterSpacing:0.4,padding:"0 8px 10px",borderBottom:"1px solid #F4F4F5",textAlign:a}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {team.map((m, i) => {
                        const fmtD = d => new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
                        const deadline = m.nearest_deadline
                          ? fmtD(m.nearest_deadline)
                          : m.earliest_assignment
                            ? fmtD(new Date(m.earliest_assignment).getTime() + 14*24*60*60*1000)
                            : "—";
                        return (
                          <tr key={i} style={{borderBottom:i<team.length-1?"1px solid #F4F4F5":"none"}}>
                            <td style={{padding:"13px 8px",verticalAlign:"middle"}}>
                              <div style={{display:"flex",alignItems:"center",gap:9}}>
                                <div style={{width:28,height:28,borderRadius:"50%",background:"#FCEBE5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#E8431A"}}>{m.name[0]}</div>
                                <div>
                                  <div style={{fontSize:11.5,fontWeight:600,color:"#26262A"}}>{m.name}</div>
                                  <div style={{fontSize:9,color:"#AFABAB"}}>{(m.role||"Sales").replace(/_/g," ")}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              <div style={{fontSize:13,fontWeight:700,fontFamily:M,color:"#3F3F46",textAlign:"center"}}>{m.assigned_shows}</div>
                            </td>
                            <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              <div style={{fontSize:13,fontWeight:700,fontFamily:M,color:"#3F3F46",textAlign:"center"}}>{m.reviewed_companies}</div>
                              {(m.approved_companies||0) > 0 && (
                                <div style={{display:"flex",gap:5,alignItems:"center",justifyContent:"center",marginTop:3}}>
                                  <span style={{fontSize:9.5,fontWeight:600,color:"#16a34a",whiteSpace:"nowrap"}}>{m.approved_companies} SQL</span>
                                </div>
                              )}
                            </td>
                            <td style={{padding:"13px 8px",verticalAlign:"middle",textAlign:"center"}}>
                              <div style={{fontSize:11,color:"#818084",textAlign:"center"}}>{deadline}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>);
          })()}
        </div>
      </div>

      {/* ══ ASSIGN MODAL ═════════════════════════════════════════════════ */}
      {assignModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}
          onClick={() => { setAssignModal(null); setAssignError(""); }}>
          <div style={{background:"#fff",borderRadius:16,padding:"28px 32px",width:420,boxShadow:"0 24px 48px -12px rgba(0,0,0,0.25)",fontFamily:F}}
            onClick={e => e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:800,color:"#26262A",marginBottom:4}}>{assignModal.isEdit ? "Edit Assignment" : "Assign Reviewer"}</div>
            <div style={{fontSize:11,color:"#AFABAB",marginBottom:20}}>{assignModal.show_name}</div>

            {/* Show picker — only when opened from section header */}
            {assignModal.showPicker && detailShows.length > 1 && (<>
              <label style={{fontSize:10,fontWeight:700,color:"#3F3F46",textTransform:"uppercase",letterSpacing:0.4,display:"block",marginBottom:4}}>Competitor Show</label>
              <select
                value={assignForm.show_id || assignModal.show_id}
                onChange={e => {
                  const picked = detailShows.find(s => s.show_id === e.target.value);
                  if (!picked) return;
                  setAssignModal(m => ({...m, show_id: picked.show_id, show_name: picked.show_name, isEdit: picked.owner_id != null}));
                  setAssignForm(f => ({...f, show_id: picked.show_id, owner_id: picked.owner_id||"", review_type: picked.review_type||"online_review", deadline: picked.deadline||""}));
                }}
                style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid #D8DADC",fontSize:12,fontFamily:F,color:"#26262A",marginBottom:14,outline:"none",boxSizing:"border-box"}}>
                {detailShows.map(s => <option key={s.show_id} value={s.show_id}>{s.show_name}</option>)}
              </select>
            </>)}

            {/* Owner */}
            <label style={{fontSize:10,fontWeight:700,color:"#3F3F46",textTransform:"uppercase",letterSpacing:0.4,display:"block",marginBottom:4}}>Owner</label>
            <select
              value={assignForm.owner_id}
              onChange={e => setAssignForm(f => ({...f, owner_id: e.target.value}))}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid #D8DADC",fontSize:12,fontFamily:F,color:"#26262A",marginBottom:14,outline:"none",boxSizing:"border-box"}}>
              <option value="">Select a reviewer…</option>
              {(isMH ? mktUsers : users).map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name} ({u.role.replace(/_/g," ")})</option>
              ))}
            </select>

            {/* Review type — sales only */}
            {!isMH && (<>
              <label style={{fontSize:10,fontWeight:700,color:"#3F3F46",textTransform:"uppercase",letterSpacing:0.4,display:"block",marginBottom:4}}>Review Type</label>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[["online_review","Online Review"],["physical_visit","Physical Visit"]].map(([v,l]) => (
                  <button key={v} onClick={() => setAssignForm(f => ({...f, review_type:v}))}
                    style={{flex:1,padding:"8px 0",borderRadius:8,border:`1.5px solid ${assignForm.review_type===v?"#E8431A":"#D8DADC"}`,background:assignForm.review_type===v?"#eff4ff":"#fff",color:assignForm.review_type===v?"#E8431A":"#818084",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>
                    {l}
                  </button>
                ))}
              </div>
            </>)}

            {/* Deadline */}
            <label style={{fontSize:10,fontWeight:700,color:"#3F3F46",textTransform:"uppercase",letterSpacing:0.4,display:"block",marginBottom:4}}>Deadline <span style={{fontWeight:400,color:"#AFABAB"}}>(optional)</span></label>
            <input type="date"
              value={assignForm.deadline}
              onChange={e => setAssignForm(f => ({...f, deadline: e.target.value}))}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid #D8DADC",fontSize:12,fontFamily:F,marginBottom:20,outline:"none",boxSizing:"border-box"}}/>

            {assignError && (
              <div style={{fontSize:10,color:"#C1121F",fontWeight:600,marginBottom:12,background:"#fdecec",padding:"8px 10px",borderRadius:6}}>{assignError}</div>
            )}

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={() => { setAssignModal(null); setAssignError(""); }}
                style={{padding:"8px 18px",borderRadius:8,border:"1px solid #D8DADC",background:"#fff",color:"#818084",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>
                Cancel
              </button>
              <button onClick={handleAssign} disabled={assignSaving}
                style={{padding:"8px 22px",borderRadius:8,border:"none",background:assignSaving?"#F3A78C":"#E8431A",color:"#fff",fontSize:12,fontWeight:700,cursor:assignSaving?"wait":"pointer",fontFamily:F}}>
                {assignSaving ? (assignModal.isEdit ? "Saving…" : "Assigning…") : (assignModal.isEdit ? "Save Changes" : "Assign")}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
